// Bug Spray — the whole board/ticket/compose experience hosted in ONE modal overlay, opened by the
// 🐞 on the grey nav bar. Ported from TopShip BugSpray.jsx, adapted to Atticus: apiFetch (returns
// parsed JSON, not a Response), inline styles on the app's CSS vars, the .modal-overlay/.modal shell,
// a BS-6-style dirty-guard so a backdrop misclick can't silently discard a typed report, and
// blob-URL thumbnails (a bare <img src> to the API carries no Authorization header → 401).
//
// View is internal state (board ↔ ticket ↔ compose) — it never touches the app router, so filing a
// bug mid-task returns the user exactly where they were. Screenshots go to the document center via
// the FILE_BUG-gated /bugs/{id}/attachments endpoints.
import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch, can } from "../api";

const STATUS = {
  triage:      { label: "Triage",        fg: "#4a5578", bg: "#eef0f7" },
  discussion:  { label: "In discussion", fg: "#3b6fd4", bg: "#eaf1fb" },
  in_progress: { label: "In progress",   fg: "#b45309", bg: "#fffbeb" },
  resolved:    { label: "✓ Resolved",    fg: "#15803d", bg: "#ecfdf3" },
  wont_do:     { label: "Won't do",      fg: "#8892b0", bg: "#eef0f7" },
};
const OPEN = ["triage", "discussion", "in_progress"];
const ORDER = ["triage", "discussion", "in_progress", "resolved", "wont_do"];

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s), now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function Chip({ status }) {
  const s = STATUS[status] || STATUS.triage;
  return <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".03em", padding: "2px 9px",
    borderRadius: 8, color: s.fg, background: s.bg, whiteSpace: "nowrap" }}>{s.label}</span>;
}
function KindTag({ kind }) {
  const bug = kind === "bug";
  return <span style={{ fontFamily: "monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: ".04em",
    padding: "1px 5px", borderRadius: 4, marginRight: 8, color: bug ? "#b91c1c" : "#3b6fd4",
    background: bug ? "#fef2f2" : "#eaf1fb" }}>{bug ? "BUG" : "FEAT"}</span>;
}

const inp = { width: "100%", border: "1px solid var(--border)", borderRadius: 5, padding: "8px 10px",
  fontSize: 12.5, color: "var(--text)", boxSizing: "border-box", fontFamily: "inherit" };

// Module-level so its identity is STABLE across Compose re-renders. When this lived inside Compose,
// every keystroke minted a new component type → React remounted both fields → Title's autoFocus
// re-fired and yanked focus off "What happened?" after one letter (BUG-3).
const Labeled = ({ label, children }) => (
  <div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text3)", marginBottom: 6 }}>{label}</div>{children}</div>
);

export default function BugReportModal({ onClose, onChanged }) {
  const canManage = can("manage_bugs");
  const [view, setView] = useState({ name: "board" });   // {name:'board'|'compose'} | {name:'ticket', id}
  const dirty = useRef(false);

  // BS-6 dirty-guard: any input inside flips dirty; backdrop/Escape confirm before discarding.
  const goBoard = useCallback(() => { dirty.current = false; setView({ name: "board" }); }, []);
  const requestClose = useCallback(() => {
    if (dirty.current && !window.confirm("Discard your changes? Anything you entered will be lost.")) return;
    onClose();
  }, [onClose]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") requestClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  return (
    <div className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
      onInput={() => { dirty.current = true; }} onChange={() => { dirty.current = true; }}>
      <div className="modal" style={{ maxWidth: 900, width: "100%", height: "80vh",
        display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div className="modal-header">
          <h3>🐞 Bug Spray {view.name === "compose" ? "· New item" : view.name === "ticket" ? "· Item" : ""}</h3>
          <button className="modal-close" onClick={requestClose} title="Close">×</button>
        </div>
        <div className="modal-body" style={{ padding: 0, flex: 1, minHeight: 0, overflowY: "auto",
          display: "flex", flexDirection: "column" }}>
          {view.name === "board" &&
            <Board canManage={canManage}
              onOpen={(id) => { dirty.current = false; setView({ name: "ticket", id }); }}
              onCompose={() => { dirty.current = false; setView({ name: "compose" }); }} />}
          {view.name === "ticket" &&
            <Ticket id={view.id} canManage={canManage} onBack={goBoard} onChanged={onChanged} />}
          {view.name === "compose" &&
            <Compose onCancel={goBoard} onDone={() => { goBoard(); onChanged && onChanged(); }} />}
        </div>
      </div>
    </div>
  );
}

/* ============================== Board ============================== */
function Board({ canManage, onOpen, onCompose }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("open_mine");

  const load = useCallback(() => {
    setItems(null);
    apiFetch(`/bugs?filter=${filter}`).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, [filter]);
  useEffect(load, [load]);

  const openCount = (items || []).filter((i) => OPEN.includes(i.status)).length;
  const resolvedCount = (items || []).filter((i) => i.status === "resolved").length;
  const unreadCount = (items || []).filter((i) => i.unread && OPEN.includes(i.status)).length;

  const Pill = ({ v, label }) => (
    <span onClick={() => setFilter(v)} style={{ fontSize: 11.5,
      color: filter === v ? "#3b6fd4" : "var(--text3)", background: filter === v ? "#eaf1fb" : "#fff",
      border: `1px solid ${filter === v ? "#3b6fd4" : "var(--border)"}`, borderRadius: 12,
      padding: "2px 11px", cursor: "pointer", fontWeight: filter === v ? 600 : 400 }}>{label}</span>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)",
        display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: "var(--text3)", maxWidth: 460 }}>
          Report anything broken or missing. Everyone can file · we reply in-thread · green check = resolved.
        </div>
        <div style={{ display: "flex", gap: 18 }}>
          <Stat v={openCount} l="open" />
          <Stat v={resolvedCount} l="resolved" green />
          <Stat v={unreadCount} l="unread" />
        </div>
      </div>
      <div style={{ padding: "10px 16px", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
        borderBottom: "1px solid var(--border)" }}>
        <Pill v="open_mine" label="Open + mine" />
        <Pill v="all" label="All" /><Pill v="bugs" label="Bugs" /><Pill v="features" label="Features" />
        <Pill v="open" label="Open" /><Pill v="mine" label="Mine" />
        <span style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={onCompose} style={{ fontSize: 12 }}>+ New</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ position: "sticky", top: 0, background: "var(--bg2,#f8fafc)", zIndex: 1 }}>
              {["", "ID", "Title", "Filed by", "Status", "Replies", "Updated"].map((h, i) => (
                <th key={i} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10.5,
                  textTransform: "uppercase", letterSpacing: ".05em", color: "var(--text3)",
                  borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null && <tr><td colSpan={7} style={{ padding: 18, color: "var(--text3)", fontSize: 12.5 }}>Loading…</td></tr>}
            {items && items.length === 0 && <tr><td colSpan={7} style={{ padding: 18, color: "var(--text3)", fontSize: 12.5 }}>Nothing filed yet. Click <b>+ New</b> to report a bug or request a feature.</td></tr>}
            {(items || []).map((r) => (
              <tr key={r.id} onClick={() => onOpen(r.id)} style={{ cursor: "pointer", borderTop: "1px solid var(--border)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f6f8fc")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <td style={{ padding: "8px 10px", textAlign: "center" }}>
                  {r.status === "resolved" ? <span style={{ color: "#15803d", fontWeight: 700 }}>✓</span>
                    : r.unread ? <span title="Unread activity" style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: "#3b6fd4" }} /> : null}
                </td>
                <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11.5, color: "var(--text2)" }}>{r.ref}</td>
                <td style={{ padding: "8px 10px", fontSize: 12.5, color: "var(--text)" }}><KindTag kind={r.kind} />{r.title}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "var(--text2)" }}>{r.created_by_name || "—"}</td>
                <td style={{ padding: "8px 10px" }}><Chip status={r.status} /></td>
                <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11.5, color: r.unread ? "#3b6fd4" : "var(--text3)", fontWeight: r.unread ? 600 : 400 }}>{r.reply_count > 0 ? `💬 ${r.reply_count}` : "—"}</td>
                <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11, color: "var(--text3)" }}>{fmtDate(r.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ v, l, green }) {
  return <div style={{ textAlign: "right" }}>
    <div style={{ fontSize: 18, fontWeight: 600, fontFamily: "monospace", color: green ? "#15803d" : "var(--text)" }}>{v}</div>
    <div style={{ fontSize: 10.5, color: "var(--text3)" }}>{l}</div>
  </div>;
}

/* ============================== Ticket ============================== */
function Ticket({ id, canManage, onBack, onChanged }) {
  const [bug, setBug] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => apiFetch(`/bugs/${id}`).then(setBug).catch(() => {}), [id]);
  useEffect(() => {
    load();
    apiFetch(`/bugs/${id}/seen`, { method: "POST" }).then(() => onChanged && onChanged()).catch(() => {});
  }, [id, load, onChanged]);

  const postReply = () => {
    if (!reply.trim() || busy) return;
    setBusy(true);
    apiFetch(`/bugs/${id}/comments`, { method: "POST", body: { body: reply.trim() } })
      .then(() => { setReply(""); return load(); }).then(() => onChanged && onChanged())
      .catch(() => {}).finally(() => setBusy(false));
  };
  const changeStatus = (status) => {
    if (busy) return;
    setBusy(true);
    apiFetch(`/bugs/${id}/status`, { method: "POST", body: { status } })
      .then(() => load()).then(() => onChanged && onChanged())
      .catch(() => {}).finally(() => setBusy(false));
  };

  if (!bug) return <div style={{ padding: 24, color: "var(--text3)" }}>Loading…</div>;

  return (
    <div style={{ padding: 16, overflowY: "auto" }}>
      <div onClick={onBack} style={{ fontFamily: "monospace", fontSize: 11.5, color: "#3b6fd4", cursor: "pointer", marginBottom: 12 }}>‹ All items</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text)", lineHeight: 1.35 }}><KindTag kind={bug.kind} />{bug.title}</div>
      <div style={{ marginTop: 7, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 11.5, color: "var(--text3)" }}>
        <span style={{ fontFamily: "monospace", color: "var(--text2)" }}>{bug.ref}</span><span>·</span><Chip status={bug.status} /><span>·</span>
        <span>filed by <b style={{ color: "var(--text2)" }}>{bug.created_by_name || "—"}</b></span>
        <span style={{ fontFamily: "monospace" }}>{fmtDate(bug.created_at)}</span>
        {bug.area && <><span>·</span><span>{bug.area}</span></>}
      </div>

      {bug.body && <Msg author={bug.created_by_name} when={bug.created_at} body={bug.body} />}
      <Attachments bugId={id} attachments={bug.attachments || []} />
      {(bug.comments || []).map((c) => (
        <Msg key={c.id} author={c.author_name} when={c.created_at} body={c.body} system={c.is_status_note} />
      ))}

      <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
        <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…"
          style={{ ...inp, minHeight: 64, resize: "vertical", lineHeight: 1.5 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 9, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={postReply} disabled={busy || !reply.trim()} style={{ fontSize: 12 }}>Reply</button>
          {canManage && bug.status !== "resolved" &&
            <button className="btn" onClick={() => changeStatus("resolved")} disabled={busy}
              style={{ fontSize: 12, background: "#15803d", color: "#fff", border: "none" }}>✓ Mark resolved</button>}
          {canManage && bug.status === "resolved" &&
            <button className="btn btn-outline" onClick={() => changeStatus("in_progress")} disabled={busy} style={{ fontSize: 12 }}>Reopen</button>}
          {canManage && (
            <select value={bug.status} onChange={(e) => changeStatus(e.target.value)} disabled={busy}
              style={{ ...inp, width: "auto", padding: "5px 8px", fontSize: 12 }}>
              {ORDER.map((s) => <option key={s} value={s}>{STATUS[s].label.replace("✓ ", "")}</option>)}
            </select>
          )}
        </div>
      </div>
    </div>
  );
}

function Msg({ author, when, body, system }) {
  const initials = (author || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (system) return (
    <div style={{ padding: "9px 2px", borderTop: "1px solid var(--border)", fontSize: 11.5, color: "var(--text3)",
      display: "flex", gap: 8, alignItems: "center" }}>
      <span style={{ fontFamily: "monospace", fontSize: 10 }}>◆</span><span>{body}</span>
      <span style={{ flex: 1 }} /><span style={{ fontFamily: "monospace", fontSize: 10.5 }}>{fmtDate(when)}</span>
    </div>
  );
  return (
    <div style={{ display: "flex", gap: 10, padding: "13px 2px", borderTop: "1px solid var(--border)" }}>
      <span style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--navy,#1a2744)", color: "#fff",
        fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)" }}>{author || "—"}</span>
          <span style={{ fontFamily: "monospace", fontSize: 10.5, color: "var(--text3)", marginLeft: "auto" }}>{fmtDate(when)}</span>
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--text2)", whiteSpace: "pre-wrap" }}>{body}</div>
      </div>
    </div>
  );
}

// Authed thumbnails: fetch bytes with the Bearer token (raw Response → blob → object URL). A bare
// <img src> to the API would send no Authorization header and 401.
function Attachments({ bugId, attachments }) {
  const [urls, setUrls] = useState({});
  const ids = attachments.map((a) => a.id).join(",");
  useEffect(() => {
    let alive = true; const made = [];
    attachments.forEach((a) => {
      apiFetch(`/bugs/${bugId}/attachments/${a.id}`, { raw: true }).then((r) => r.blob()).then((b) => {
        if (!alive) return;
        const u = URL.createObjectURL(b); made.push(u);
        setUrls((p) => ({ ...p, [a.id]: u }));
      }).catch(() => {});
    });
    return () => { alive = false; made.forEach(URL.revokeObjectURL); };
  }, [bugId, ids]);
  if (!attachments.length) return null;
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "10px 2px", borderTop: "1px solid var(--border)" }}>
      {attachments.map((a) => urls[a.id]
        ? <a key={a.id} href={urls[a.id]} target="_blank" rel="noreferrer">
            <img alt={a.filename || "screenshot"} src={urls[a.id]}
              style={{ maxHeight: 120, borderRadius: 6, border: "1px solid var(--border)" }} /></a>
        : <span key={a.id} style={{ fontSize: 11.5, color: "var(--text3)" }}>📎 {a.filename}</span>)}
    </div>
  );
}

/* ============================== Compose ============================== */
function Compose({ onCancel, onDone }) {
  const [kind, setKind] = useState("bug");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [area, setArea] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const created = await apiFetch("/bugs", { method: "POST",
        body: { kind, title: title.trim(), body: body.trim() || null, area: area.trim() || null } });
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        // raw:true → browser sets the multipart boundary; wrapper doesn't JSON-stringify FormData.
        await apiFetch(`/bugs/${created.id}/attachments`, { method: "POST", raw: true, body: fd });
      }
      onDone();
    } catch (e) {
      setErr((e && e.message) || "Something went wrong");
      setBusy(false);
    }
  };

  const seg = (v, label) => (
    <button key={v} onClick={() => setKind(v)} style={{ fontSize: 12, fontWeight: 600,
      border: `1px solid ${kind === v ? (v === "bug" ? "#b91c1c" : "#3b6fd4") : "var(--border)"}`,
      background: kind === v ? (v === "bug" ? "#fef2f2" : "#eaf1fb") : "#fff",
      color: kind === v ? (v === "bug" ? "#b91c1c" : "#3b6fd4") : "var(--text3)",
      borderRadius: 6, padding: "8px 16px", cursor: "pointer" }}>{label}</button>
  );
  return (
    <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 13, overflowY: "auto" }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {seg("bug", "🐞 Bug — something's broken")}{seg("feature", "✦ Feature — something's missing")}
      </div>
      <Labeled label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
          placeholder={kind === "bug" ? "e.g. Claim photo won't upload on the Documents tab" : "e.g. Add a bulk-reassign button to Workbench"} style={inp} />
      </Labeled>
      <Labeled label={kind === "bug" ? "What happened?" : "What do you need?"}>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} style={{ ...inp, minHeight: 84, resize: "vertical", lineHeight: 1.5 }} />
      </Labeled>
      <Labeled label="Where in the app? (optional)">
        <input value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Claim → Documents tab" style={inp} />
      </Labeled>
      <Labeled label="Screenshots (optional)">
        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple
          onChange={(e) => setFiles([...e.target.files])} style={{ fontSize: 12 }} />
        {files.length > 0 && <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 4 }}>{files.length} image{files.length > 1 ? "s" : ""} attached</div>}
      </Labeled>
      {err && <div style={{ fontSize: 12, color: "#b91c1c", background: "#fef2f2", padding: "6px 10px", borderRadius: 5 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className="btn btn-primary" onClick={submit} disabled={busy || !title.trim()} style={{ fontSize: 12.5 }}>{busy ? "Filing…" : "Submit"}</button>
        <button className="btn btn-outline" onClick={onCancel} disabled={busy} style={{ fontSize: 12.5 }}>Cancel</button>
      </div>
    </div>
  );
}
